import { Either, List } from 'monet';
import ServicePriceRubles from '@/storage/ServicePriceRubles';
import LeftResponse from '../LeftResponse';
import { makeDiscount, PositionDiscount } from './VisitGateway';

/**
 * @description Ожидаемый тип контента docx.
 * @type {string}
 */
const ACCEPT_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * @description Способ оплаты.
 * @param {number} amount Сумма в копейках.
 * @param {string} type   Тип оплаты.
 * @constructor
 */
export function PaymentType(amount, type) {
  this.amount = amount;
  this.type = type;

  /**
   * @description К запросу.
   * @returns {any}
   */
  this.toQuery = () => ({
    amount: this.amount,
    type: this.type,
  });
}

/**
 * @description Запрос на создание комментария к счёту.
 *
 * @param comment   Комментарий
 * @param invoiceId Id счёта.
 * @constructor
 */
export function SetInvoiceCommentRequest(comment, invoiceId) {
  this.invoiceId = invoiceId;
  this.comment = comment;

  /**
   * @description К запросу.
   * @returns {any}
   */
  this.toQuery = () => ({
    comment: this.comment,
  });
}

/**
 * @description Позиция.
 * @param {boolean}          byVHIPolicy
 * @param {number}           cost
 * @param {number}           count
 * @param {boolean}          complaint
 * @param {PositionDiscount} discount
 * @param {number|null}      serviceId
 * @param {string}           serviceName
 * @param {number|null}      id
 * @param {string}           status
 * @param {string}           itemType
 * @param {string}           vat
 * @param {string}           categoryName
 * @param {number}           refund
 */
export function Position(
  key,
  discount = PositionDiscount.default(),
  byVHIPolicy = false,
  cost = 0,
  count = 1,
  complaint = false,
  serviceId = null,
  serviceName = '',
  itemType = '',
  vat = '',
  id = null,
  status = 'Не оплачена',
  categoryName = '',
  refund = 0,
  paid = 0,
  serviceClinicCode = '-',
  serviceDentalCode = '-',
) {
  this.byVHIPolicy = byVHIPolicy;
  this.categoryName = categoryName;
  this.cost = cost;
  this.count = count;
  this.complaint = complaint;
  this.discount = discount;
  this.id = id;
  this.itemType = itemType;
  this.serviceId = serviceId;
  this.serviceName = serviceName;
  this.status = status;
  this.vat = vat;
  this.key = key;
  this.refund = refund;
  this.paid = paid;
  this.serviceClinicCode = serviceClinicCode;
  this.serviceDentalCode = serviceDentalCode;

  const amount = new ServicePriceRubles(cost).mul(count);
  this.getAmount = () => amount;
  this.getRefund = () => new ServicePriceRubles(refund);
  const isFullDiscounted = byVHIPolicy || complaint;
  this.isFullDiscounted = () => isFullDiscounted;
  const calcPrice = () => {
    if (isFullDiscounted) {
      return {
        discount: amount,
        final: ServicePriceRubles.zero(),
      };
    }
    return this.discount.apply(amount);
  };
  const { discount: discountValue, final: finalPrice } = calcPrice();
  this.getDiscount = () => discountValue;
  this.getFinalPrice = () => finalPrice;

  const copyWith = (props) => new this.constructor(
    this.key,
    props.discount === undefined ? this.discount : props.discount,
    props.byVHIPolicy === undefined ? this.byVHIPolicy : props.byVHIPolicy,
    props.cost === undefined ? this.cost : props.cost,
    props.count === undefined ? this.count : props.count,
    props.complaint === undefined ? this.complaint : props.complaint,
    props.serviceId === undefined ? this.serviceId : props.serviceId,
    props.serviceName === undefined ? this.serviceName : props.serviceName,
    props.itemType === undefined ? this.itemType : props.itemType,
    props.vat === undefined ? this.vat : props.vat,
    this.id,
    this.status,
    categoryName === undefined ? this.categoryName : props.categoryName,
    this.serviceClinicCode,
    this.serviceDentalCode,
  );
  this.setByVHIPolicy = (value) => copyWith({ byVHIPolicy: value });
  this.setComplaint = (newComplaint, newByVHIPolicy) => copyWith({
    complaint: newComplaint,
    byVHIPolicy: newByVHIPolicy,
  });
  this.setDiscount = (value) => copyWith({ discount: value });
  this.setCount = (value) => copyWith({ count: value });
  this.setService = (
    newServiceId,
    newServiceName,
    newCost,
    newVat,
    newItemType,
    newCategoryName,
  ) => copyWith({
    serviceId: newServiceId,
    serviceName: newServiceName,
    cost: newCost,
    vat: newVat,
    itemType: newItemType,
    categoryName: newCategoryName,
  });
  /**
   * @description К запросу.
   * @returns {any}
   */
  this.toQuery = () => ({
    by_vhi_policy: this.byVHIPolicy,
    cost: this.cost,
    count: this.count,
    complaint: this.complaint,
    discount: this.discount.getValue(),
    discount_type: this.discount.getType(),
    service_id: this.serviceId,
  });
}

/**
 * @description Запрос на создание.
 * @param {number}     employeeId Идентификатор сотрудника.
 * @param {number}     patientId  Идентификатор пациента.
 * @param {Position[]} services   Позиции.
 * @constructor
 */
export function CreationRequest(employeeId, patientId, services) {
  this.getPatientId = () => patientId;

  this.toQuery = () => ({
    employee_id: employeeId,
    services: services.map((service) => service.toQuery()),
  });
}

/**
 * @description Запрос на редактирование.
 * @param {number}     employeeId Идентификатор сотрудника.
 * @param {number}     invoiceId  Идентификатор счета.
 * @param {Position[]} services   Позиции.
 * @constructor
 */
export function EditRequest(employeeId, invoiceId, services) {
  this.getInvoiceId = () => invoiceId;
  this.toQuery = () => ({
    employee_id: employeeId,
    services: services.map((service) => ({ ...service.toQuery(), id: service.id })),
  });
}

/**
 * @description Запрос на оплату счета.
 * @param {number}        invoiceId    Идентификатор счета.
 * @param {PaymentType[]} paymentTypes Типы оплат.
 * @param {number[]}      positionIds  Идентификатор позиций.
 * @constructor
 */
export function PayRequest(invoiceId, cashboxId, paymentTypes, positionIds) {
  this.getInvoiceId = () => invoiceId;
  this.toQuery = () => ({
    cashbox_id: cashboxId,
    payments: paymentTypes.map((payment) => payment.toQuery()),
    position_ids: positionIds,
  });
}

/**
 * @description Запрос на оплату счета.
 * @param {number}        invoiceId    Идентификатор счета.
 * @param {PaymentType[]} paymentTypes Типы оплат.
 * @param {any[]}         positions    Позиции.
 * @constructor
 */
export function RefundRequest(invoiceId, cashboxId, paymentTypes, positions) {
  this.getInvoiceId = () => invoiceId;
  this.toQuery = () => ({
    cashbox_id: cashboxId,
    payments: paymentTypes.map((payment) => payment.toQuery()),
    positions: positions.map(({ id, count }) => ({ id, count })),
  });
}

/**
 * @description Запрос на пополнение аванса.
 * @param {number}        patientId    Идентификатор пациента.
 * @param {PaymentType[]} paymentTypes Типы оплат.
 * @param {number}        cashboxId    Идентификатор кассы.
 * @constructor
 */
export function AdvanceRechargeRequest(patientId, paymentTypes, cashboxId) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    payment_types: paymentTypes.map((payment) => payment.toQuery()),
    cashbox_id: cashboxId,
  });
}

/**
 * @description Запрос на предпросмотр чеков оплаты.
 * @param {number}        invoiceId   Идентификатор счета.
 * @param {PaymentType[]} paymentTypes Типы оплат.
 * @param {number[]}      positionIds Идентификаторы позиций.
 * @constructor
 */
export function PreviewRequest(invoiceId, paymentTypes, positionIds) {
  this.getInvoiceId = () => invoiceId;
  this.getPaymentTypes = () => paymentTypes;
  this.getPositionIds = () => positionIds;
  this.toQuery = () => ({
    payments: paymentTypes.map((payment) => payment.toQuery()),
    position_ids: positionIds,
  });
}

/**
 * @description Запрос на внесение аванса на счет.
 * @param {number} invoiceId Идентификатор счета.
 * @param {number} amount Сумма.
 * @constructor
 */
export function PrepayRequest(invoiceId, amount) {
  /**
   * @description Получить идентификатор счета.
   * @returns {number}
   */
  this.getInvoiceId = () => invoiceId;

  /**
   * @description К запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    amount,
  });
}

/**
 * @description Шлюз счетов.
 * @param {Axios} axios HTTP клиент.
 */
export default function InvoiceGateway(axios) {
  /**
   * @description Пополнить аванс.
   * @param {AdvanceRechargeRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.advanceRecharge = (request) => axios
    .post(`/api/patients/${request.getPatientId()}/advance/recharge`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить способ оплаты для операции по счету.
   * @param {string} operationId Идентификатор операции.
   * @param {string} paymentMode Способ оплаты.
   * @returns {Promise<Either>}
   */
  this.changePaymentModeForAdvanceRechargeOperation = (operationId, paymentMode) => axios
    .put(
      `/api/advance-recharge-operations/${operationId}/payment-mode`,
      { payment_mode: paymentMode },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить способ оплаты для операции по счету.
   * @param {string} operationId Идентификатор операции.
   * @param {string} paymentMode Способ оплаты.
   * @returns {Promise<Either>}
   */
  this.changePaymentModeForInvoiceOperation = (operationId, paymentMode) => axios
    .put(
      `/api/invoice-operations/${operationId}/payment-mode`,
      { payment_mode: paymentMode },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && (
          error.response.status === 400
          || error.response.status === 403
        )) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать счет.
   * @param {CreationRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.create = (request) => axios
    .post(`/api/patients/${request.getPatientId()}/invoices`, request.toQuery())
    .then(
      async (response) => {
        const { data } = response.data;
        const { message } = data;
        const invoiceId = data.invoice_id;
        return Either.right({ message, invoiceId });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать по визиту.
   * @param {number} visitId Идентификатор визита.
   * @returns {Promise<Either>}
   */
  this.createByVisitId = (visitId) => axios
    .post(`/api/visits/${visitId}/invoices`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать по записи на прием.
   * @param {number} visitId Идентификатор записи на прием.
   * @returns {Promise<Either>}
   */
  this.createByAppointmentId = (appointmentId) => axios
    .post(`/api/appointments/${appointmentId}/invoices`)
    .then(
      async (response) => {
        const { data } = response.data;
        return Either.right({
          invoiceId: data.invoice_id,
          message: data.message,
          patientId: data.patient_id,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование.
   * @param {EditRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.edit = (request) => axios
    .put(`/api/invoices/${request.getInvoiceId()}`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на принудительное проведение оплаты.
   * @param {number} commandId
   * @returns {Promise<Either<any,LeftResponse>>}
   */
  this.forcePayment = (commandId) => axios
    .put(`/api/online-check/commands/${commandId}/force-payment`)
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на оплату.
   * @param {PayRequest} request Запрос.
   * @returns {Promise<Either<any,LeftResponse>>}
   */
  this.pay = (request) => axios
    .post(`/api/invoices/${request.getInvoiceId()}/pay`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на возврат средств.
   * @param {RefundRequest} request
   * @returns {Promise<Either<any,LeftResponse>>}
   */
  this.refundManualCashbox = (request) => axios
    .post(`/api/invoices/${request.getInvoiceId()}/refund`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на предпросмотр чеков оплаты.
   * @param {PreviewRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.previewPaymentChecks = (request) => axios
    .post(`/api/invoices/${request.getInvoiceId()}/preview-payment-checks`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на возврат.
   * @param {string} invoiceId Идентификатор счета.
   * @param {string} secret    Секретный ключ.
   * @returns {Promise<Either>}
   */
  this.refund = (paymentId, secret) => axios
    .post(`/api/online-check/payments/${paymentId}/refund`, { secret })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить по идентификатору.
   * @param {number} invoiceId Идентификатор счета.
   * @returns {Promise<Either>}
   */
  this.viewById = (invoiceId) => axios
    .get(`/api/invoices/${invoiceId}`)
    .then(
      async (response) => {
        const { data } = response;
        const positionMapper = (position) => new Position(
          '',
          makeDiscount(
            position.discount_type,
            position.discount,
          ),
          position.by_vhi_policy,
          position.cost,
          position.count,
          position.complaint,
          position.service_id,
          position.service_name,
          position.item_type,
          position.vat,
          position.id,
          position.status,
          position.category_name,
          position.refund,
          position.paid,
          position.service_clinic_code,
          position.service_dental_code,
        );
        const positionForPayment = (position) => ({
          id: position.id,
          categoryName: position.category_name,
          complaint: position.complaint,
          cost: position.cost,
          count: position.count,
          debt: position.debt,
          discount: makeDiscount(
            position.discount_type,
            position.discount,
          ),
          serviceName: position.service_name,
        });
        const positionForRefund = (position) => ({
          id: position.id,
          categoryName: position.category_name,
          complaint: position.complaint,
          cost: position.cost,
          count: position.count,
          amount: position.amount,
          discount: makeDiscount(
            position.discount_type,
            position.discount,
          ),
          serviceName: position.service_name,
        });
        const invoice = {
          actions: data.actions,
          advance: data.available_advance,
          cashboxesForPayment: data.cashboxes_for_payment,
          commandId: data.command_id,
          debt: data.debt,
          documents: data.documents,
          discountType: data.discount_type,
          employee: data.employee,
          operationList: data.operation_list,
          paid: data.paid,
          paymentAdvance: data.payment_advance,
          paymentAmount: data.payment_amount,
          paymentCash: data.payment_cash,
          paymentCashless: data.payment_cashless,
          paymentStatus: data.payment_status,
          positions: data.positions.map(positionMapper),
          positionsForPayments: data
            .positions_for_payments
            .map(positionForPayment),
          positionsForRefund: data
            .positions_for_refund
            .map(positionForRefund),
          title: data.title,
          totalAdvance: data.total_advance,
          totalDebt: data.total_debt,
          totalPaid: data.total_paid,
          totalRefund: data.total_refund,
          visit: data.visit,
          comment: data.comment,
          columns: data.columns,
          isBranch: data.is_branch,
        };
        return Either.right(invoice);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить список счетов пациента.
   * @param {number} patientId Идентификатор пациента.
   * @param {string} status    Статус.
   * @param {string} sort      Сортировка.
   * @returns {Promise<Either>}
   */
  this.viewList = (patientId, status, sort) => axios
    .get(`/api/patients/${patientId}/invoices`, { params: { status, sort } })
    .then(
      async (response) => {
        const { actions } = response.data.data;
        const invoiceList = (response.data.data.items || []).map((invoice) => ({
          amount: invoice.amount,
          authorName: invoice.author_name,
          date: invoice.created_at,
          employeeName: invoice.employee_name,
          id: invoice.id,
          number: invoice.number,
          status: invoice.status,
          statusId: invoice.status_id,
          visitDate: invoice.visit_date,
          refund: invoice.refund,
          comment: invoice.comment,
          branchName: invoice.branch_name,
        }));
        const patient = {
          advance: response.data.data.advance,
          commandId: response.data.data.command_id,
          debt: response.data.data.debt,
          paid: response.data.data.paid,
        };
        return Either.right({
          actions,
          invoiceList: List.fromArray(invoiceList),
          patient,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для выставления счета.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewCreation = (patientId) => axios
    .get(`/api/patients/${patientId}/invoice-creations`)
    .then(
      async (response) => {
        const { employees, services } = response.data.data;
        return Either.right({ employees, services });
      },
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Показать изменение способа оплаты для операции пополнения аванса.
   * @param {number} operationId Идентификатор операции.
   * @returns {Promise<Either>}
   */
  this.viewPaymentModeChangeForAdvanceRechargeOperation = (operationId) => axios
    .get(`/api/advance-recharge-operations/${operationId}/payment-mode-change`)
    .then(
      (response) => {
        const rechargeOperation = {
          id: response.data.operation_id,
          mode: response.data.mode,
          totalPaid: response.data.total_paid,
        };
        return Either.of({
          availableModes: response.data.available_modes,
          rechargeOperation,
        });
      },
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать изменение способа оплаты для операции по счету.
   * @param {string} operationId Идентификатор операции.
   * @returns {Promise<Either>}
   */
  this.viewPaymentModeChangeForInvoiceOperation = (operationId) => axios
    .get(`/api/invoice-operations/${operationId}/payment-mode-change`)
    .then(
      (response) => {
        const paymentOperation = {
          id: response.data.operation_id,
          mode: response.data.mode,
          totalPaid: response.data.total_paid,
        };
        return Either.of({
          availableModes: response.data.available_modes,
          paymentOperation,
        });
      },
      (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 400 || error.response.status === 403)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать детализацию по авансу пациента.
   * @param {string} patientId Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewPatientAdvanceDetails = (patientId) => axios
    .get(`/api/patients/${patientId}/advance-details`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать аванс пациента для списания.
   * @param {string} patientId Идентификатор пациента.
   * @returns {Promise<Either>}
   */
  this.viewPatientAdvanceForWithdrawal = (patientId) => axios
    .get(`/api/patients/${patientId}/advance-for-withdrawal`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 403 || error.response.status === 404)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на внесение аванса.
   * @param {PrepayRequest} request Запрос.
   * @returns {Either<Object,LeftResponse>}
   */
  this.prepay = (request) => axios
    .put(`/api/invoices/${request.getInvoiceId()}/prepay`, request.toQuery())
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить квитанцию об оплате для печати.
   * @param {number} operationId Идентификатор операции.
   * @returns {Promise<Either>}
   */
  this.printPaymentSlip = (operationId) => axios
    .get(`/api/invoice-operations/${operationId}/payment-slip`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить квитанцию возврата для печати.
   * @param {number} operationId Идентификатор операции.
   * @returns {Promise<Either>}
   */
  this.printRefundSlip = (operationId) => axios
    .get(`/api/invoice-operations/${operationId}/refund-slip`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Распечатать акт.
   * @param {number} invoiceId
   * @param {any}    request
   * @returns {Promise<Either>}
   */
  this.printAct = (invoiceId, request) => axios
    .post(
      `/api/invoices/${invoiceId}/act`,
      request,
    ).then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Скачать акт.
   * @param {number} invoiceId
   * @param {any}    request
   * @returns {Promise<Either>}
   */
  this.downloadAct = (invoiceId, request) => axios
    .post(
      `/api/invoices/${invoiceId}/act`,
      request,
      { headers: { accept: ACCEPT_DOCX }, responseType: 'blob' },
    ).then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          return Either.left(data);
        }
        throw error;
      },
    );

  /**
   * @description Списать с аванса пациента.
   * @param {string}            patientId  Идентификатор пациента.
   * @param {AdvanceWithdrawal} withdrawal Списание.
   * @returns {Promise<Either>}
   */
  this.withdrawPatientAdvance = (patientId, withdrawal) => axios
    .post(
      `/api/patients/${patientId}/advance-withdrawals`,
      {
        cashbox_id: withdrawal.cashboxId,
        rubles: withdrawal.rubles,
        mode: withdrawal.mode,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError
          && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Вставить комментарий счёта.
   *
   * @param {SetInvoiceCommentRequest} request Запрос
   * @return {*}
   */
  this.setInvoiceComment = (request) => axios
    .put(`/api/set-invoice-comment/${request.invoiceId}`, request.toQuery())
    .then(
      (response) => Either.of(response.data.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
